import { useState, useEffect, Dispatch, SetStateAction, useCallback } from 'react';
import useUnmount from '../hooks/useUnmount';
import { LooksInfusionSliderVariant } from './types';
import { TrackPDPLooksInfusionActions } from 'Common/ga/types';
import useGa from 'Client/hooks/useGa';
import { useLaunchDarklyTracker } from 'Client/LaunchDarklyTracker';
import { useBellaSelector } from 'Client/redux/hooks';

export enum LooksInfusionTrackLabel {
    LAUNCH_DARKLY_ADD_TO_CART_METRIC = 'bella-pdp-add-to-cart-look-infusion',
    LAUNCH_DARKLY_ADD_TO_WISHLIST_METRIC = 'bella-pdp-add-to-wishlist-look-infusion',
}

const useGaTracking = () => {
    const gaTracking = useGa();

    const trackLookInfusionClicks = (label: string) => {
        gaTracking.trackPDPInteractionEvent({
            action: TrackPDPLooksInfusionActions.LookInfusionClick,
            label,
        });
    };
    const trackLookSliderClicks = (label: string) => {
        gaTracking.trackPDPInteractionEvent({
            action: TrackPDPLooksInfusionActions.LookSliderClick,
            label,
        });
    };

    const trackLookInfusionImpression = (label: string) => {
        gaTracking.trackPDPInteractionEvent({
            action: TrackPDPLooksInfusionActions.LookInfusionImpression,
            label,
        });
    };

    const trackLookSliderImpression = (label: string) => {
        gaTracking.trackPDPInteractionEvent({
            action: TrackPDPLooksInfusionActions.LookSliderImpression,
            label,
        });
    };

    const trackLookSliderWishlist = (action: string) => {
        gaTracking.trackPDPInteractionEvent({
            action,
            label: 'PDP Look slider',
        });
    };

    const trackLookInfusionVariation = (variant: LooksInfusionSliderVariant) => {
        gaTracking.trackExperiment({
            action: TrackPDPLooksInfusionActions.LookInfusionABTestExperiment,
            label: variant,
        });
    };

    const trackExperimentAddToCartRate = () => {
        gaTracking.trackExperiment({
            action: TrackPDPLooksInfusionActions.LookInfusionABTestExperiment,
            label: 'pdp-add-to-cart',
        });
    };

    const trackExperimentAddToWishlist = () => {
        gaTracking.trackExperiment({
            action: TrackPDPLooksInfusionActions.LookInfusionABTestExperiment,
            label: 'pdp-add-to-wishlist',
        });
    };

    const trackLooksSliderHomePageClick = (trackingName: string) => {
        gaTracking.homepage.trackLooksSliderTrackingClick(trackingName);
    };

    const trackLookSliderHomePageImpression = () => {
        gaTracking.homepage.trackSliderImpression('Shop the Look slider impression');
    };

    return {
        trackLookInfusionClicks,
        trackLookInfusionImpression,
        trackExperimentAddToCartRate,
        trackExperimentAddToWishlist,
        trackLookInfusionVariation,
        trackLookSliderClicks,
        trackLookSliderImpression,
        trackLookSliderWishlist,
        trackLooksSliderHomePageClick,
        trackLookSliderHomePageImpression,
    };
};

const useTrackCurrentProduct = (): [boolean, Dispatch<SetStateAction<boolean>>] => {
    const [trackCurrentProduct, setTrackCurrentProduct] = useState(false);

    useUnmount(useCallback(() => setTrackCurrentProduct(false), []));

    return [trackCurrentProduct, setTrackCurrentProduct];
};

interface UseTrackingImpressionEventParams {
    isElementInView: boolean;
}

const useTrackingImpressionEvent = ({ isElementInView }: UseTrackingImpressionEventParams) => {
    const { trackLookInfusionImpression } = useGaTracking();
    const { sku } = useBellaSelector(state => state.productdetails.productdetail);
    const { trackingName } = useBellaSelector(state => state.productLooks.looks?.[0] || {});
    const [trackCurrentProduct, setTrackCurrentProduct] = useTrackCurrentProduct();

    useEffect(() => {
        if (isElementInView && trackCurrentProduct !== true) {
            trackLookInfusionImpression(`PDP: ${sku}, Look: ${trackingName}`);
            setTrackCurrentProduct(true);
        }
    }, [isElementInView, setTrackCurrentProduct, sku, trackCurrentProduct, trackLookInfusionImpression, trackingName]);
};

interface UseTrackingVariationEventParams {
    isElementInView: boolean;
    variant: LooksInfusionSliderVariant;
}

const useTrackingVariationEvent = ({ isElementInView, variant }: UseTrackingVariationEventParams) => {
    const launchDarklyTracker = useLaunchDarklyTracker();
    const [trackCurrentProduct, setTrackCurrentProduct] = useTrackCurrentProduct();

    useEffect(() => {
        if (isElementInView && trackCurrentProduct !== true) {
            launchDarklyTracker.trackVariation(TrackPDPLooksInfusionActions.LookInfusionABTestExperiment, variant);
            setTrackCurrentProduct(true);
        }
    }, [isElementInView, launchDarklyTracker, setTrackCurrentProduct, trackCurrentProduct, variant]);
};

const useTrackingAddToCartMetricEvent = () => {
    const { trackExperimentAddToCartRate } = useGaTracking();
    const launchDarklyTracker = useLaunchDarklyTracker();

    const trackMetricEvent = () => {
        launchDarklyTracker.trackImportantMetric(LooksInfusionTrackLabel.LAUNCH_DARKLY_ADD_TO_CART_METRIC);
        trackExperimentAddToCartRate();
    };

    return trackMetricEvent;
};

const useTrackingAddToWishlistMetricEvent = () => {
    const { trackExperimentAddToWishlist } = useGaTracking();
    const launchDarklyTracker = useLaunchDarklyTracker();

    const trackMetricEvent = () => {
        launchDarklyTracker.trackMetric(LooksInfusionTrackLabel.LAUNCH_DARKLY_ADD_TO_WISHLIST_METRIC);
        trackExperimentAddToWishlist();
    };

    return trackMetricEvent;
};

export {
    useGaTracking,
    useTrackingImpressionEvent,
    useTrackingVariationEvent,
    useTrackingAddToCartMetricEvent,
    useTrackingAddToWishlistMetricEvent,
};
